import React, { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import { connect } from 'react-redux';
import {
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  CssBaseline,
  Box,
  Tooltip,
  Divider,
  Avatar,
  Typography,
} from '@mui/material';
import MuiDrawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Modal from '@mui/material/Modal';
import { useLocation, useNavigate } from 'react-router-dom';
import useTranslation from '../../customHooks/translations';
import { getFreakIcon } from '../../utils/freakUtils';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import CampaignIcon from '@mui/icons-material/Campaign';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import SportsMmaIcon from '@mui/icons-material/SportsMma';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import HubIcon from '@mui/icons-material/Hub';
import ChatIcon from '@mui/icons-material/Chat';
import { CHAT_ENABLED } from '../../constants/featureFlags';
import AddIcon from '@mui/icons-material/Add';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SettingsIcon from '@mui/icons-material/Settings';
import { Badge as BaseBadge, badgeClasses } from '@mui/base/Badge';
import agent from '../../agent';
import {
  BOTTOM_NAVIGATION_LOADED,
  BOTTOM_NAVIGATION_SET_UNREAD_MESSAGES,
  LEFT_NAVIGATION_OPEN,
} from '../../constants/actionTypes';
import { DEFAULT_THEME_COLOR, DEFAULT_GRADIENT } from '../../constants/theme';
import FiltersComponent from '../Filters/FiltersComponent';
import { useAuth0 } from '@auth0/auth0-react';
import QuickCreateButton from '../QuickCreate/QuickCreateButton';

const DRAWER_WIDTH = 240;
const COLLAPSED_WIDTH = 68;

const Badge = styled(BaseBadge)`
  box-sizing: border-box;
  position: relative;
  display: flex;
  align-self: center;

  & .${badgeClasses.badge} {
    z-index: auto;
    position: absolute;
    top: 0;
    right: 0;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    color: #fff;
    font-weight: 700;
    font-size: 11px;
    line-height: 18px;
    text-align: center;
    border-radius: 9px;
    background: ${DEFAULT_THEME_COLOR};
    transform: translate(50%, -50%);
    transform-origin: 100% 0;
  }

  & .${badgeClasses.invisible} {
    opacity: 0;
    pointer-events: none;
  }
`;

const openedMixin = (theme) => ({
  width: DRAWER_WIDTH,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: COLLAPSED_WIDTH,
});

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme }) => ({
    width: DRAWER_WIDTH,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    variants: [
      {
        props: ({ open }) => open,
        style: {
          ...openedMixin(theme),
          '& .MuiDrawer-paper': openedMixin(theme),
        },
      },
      {
        props: ({ open }) => !open,
        style: {
          ...closedMixin(theme),
          '& .MuiDrawer-paper': closedMixin(theme),
        },
      },
    ],
    '& .MuiDrawer-paper': {
      background: 'linear-gradient(180deg, #0d1b2a 0%, #091420 100%)',
      borderRight: '1px solid rgba(255,255,255,0.06)',
    },
  }),
);

const NavItem = ({ label, icon, active, onClick, drawerOpen, badgeCount }) => (
  <Tooltip title={!drawerOpen ? label : ''} placement="right" arrow>
    <ListItemButton
      onClick={onClick}
      sx={{
        mx: 1,
        borderRadius: 2,
        mb: 0.25,
        px: drawerOpen ? 1.5 : 1,
        minHeight: 44,
        backgroundColor: active ? 'rgba(54,201,162,0.12)' : 'transparent',
        borderLeft: `3px solid ${active ? DEFAULT_THEME_COLOR : 'transparent'}`,
        transition: 'background 0.15s, border-color 0.15s',
        '&:hover': {
          backgroundColor: active ? 'rgba(54,201,162,0.16)' : 'rgba(54,201,162,0.07)',
          '& .nav-icon': { color: DEFAULT_THEME_COLOR },
          '& .nav-text': { color: 'white' },
        },
      }}
    >
      <ListItemIcon
        className="nav-icon"
        sx={{
          minWidth: drawerOpen ? 38 : 'auto',
          color: active ? DEFAULT_THEME_COLOR : 'rgba(255,255,255,0.5)',
          transition: 'color 0.15s',
          '& svg': { fontSize: 22 },
        }}
      >
        {badgeCount > 0 ? <Badge badgeContent={badgeCount}>{icon}</Badge> : icon}
      </ListItemIcon>
      {drawerOpen && (
        <ListItemText
          className="nav-text"
          primary={label}
          primaryTypographyProps={{
            sx: {
              color: active ? 'white' : 'rgba(255,255,255,0.65)',
              fontWeight: active ? 600 : 400,
              fontSize: '0.875rem',
              transition: 'color 0.15s',
            },
          }}
        />
      )}
    </ListItemButton>
  </Tooltip>
);

const LeftNavigationComponent = (props) => {
  const translation = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [filtersBadgeCount, setFiltersBadgeCount] = useState(0);

  const { isAuthenticated } = useAuth0();
  const context = props.context || props?.currentUser?.context || 'wof';
  const pathname = location?.pathname || '';

  const fetchNotificationsCount = () => {
    if (isAuthenticated) {
      props.onLoad(Promise.all([agent.Notifications.count()]));
    }
  };

  useEffect(() => {
    fetchNotificationsCount();
  }, []);

  useEffect(() => {
    let count = 0;
    if (props.sortByDateFilter && props.sortByDateFilter !== 'none') count++;
    if (props.filterByStatusFilter && props.filterByStatusFilter !== 'none') count++;
    if (props.sortByLikesFilter && props.sortByLikesFilter !== 'none') count++;
    if (props.filterByDateFromFilter && props.filterByDateFromFilter !== 'none') count++;
    if (props.filterByDateToFilter && props.filterByDateToFilter !== 'none') count++;
    if (props.filterByParticipantFilter && props.filterByParticipantFilter !== 'none') count++;
    setFiltersBadgeCount(count);
  }, [
    props.sortByLikesFilter,
    props.sortByDateFilter,
    props.filterByNameFilter,
    props.filterByDateFromFilter,
    props.filterByDateToFilter,
    props.filterByStatusFilter,
    props.filterByParticipantFilter,
  ]);

  const handleToggle = () => {
    const next = !open;
    setOpen(next);
    props.setLeftNavigationOpen(next);
  };

  const showAddButton = ['/freak', '/', '/places', '/freak-hub'].includes(pathname);
  const showFilters = ['/freak', '/places'].includes(pathname);
  const notifCount = props.common?.notificationsCount || 0;

  const addTarget = () => {
    if (pathname === '/freak') navigate('/freak-editor');
    else if (pathname === '/') navigate('/editor');
    else if (pathname === '/places') navigate('/places-editor');
    else if (pathname === '/freak-hub') navigate('/freak-hub-editor');
  };

  const renderAddButton = onClick => (
    <Tooltip title={!open ? translation.leftNavigation.add : ''} placement="right" arrow>
      <Box
        onClick={onClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: open ? 1.5 : 1,
          py: 0.75,
          borderRadius: 2,
          background: DEFAULT_GRADIENT,
          cursor: 'pointer',
          transition: 'opacity 0.15s',
          '&:hover': { opacity: 0.85 },
        }}
      >
        <AddIcon sx={{ color: 'white', fontSize: 22, flexShrink: 0 }} />
        {open && (
          <Typography sx={{ color: 'white', fontWeight: 600, fontSize: '0.875rem' }}>
            {translation.leftNavigation.add}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );

  if (!isAuthenticated) return null;

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <Drawer variant="permanent" open={open}>

        {/* Header: toggle + logo */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 1,
            py: 1.25,
            gap: 1,
            borderBottom: '1px solid rgba(255,255,255,0.06)',
            minHeight: 60,
          }}
        >
          <IconButton
            onClick={handleToggle}
            size="small"
            sx={{
              color: 'rgba(255,255,255,0.5)',
              '&:hover': { color: 'white', backgroundColor: 'rgba(255,255,255,0.06)' },
              flexShrink: 0,
            }}
          >
            {open ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </IconButton>
          {open && (
            <Typography
              onClick={() => navigate('/freak')}
              sx={{
                background: `linear-gradient(90deg, ${DEFAULT_THEME_COLOR}, #4facfe)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                fontWeight: 800,
                fontSize: '1.1rem',
                letterSpacing: '-0.01em',
                cursor: 'pointer',
                userSelect: 'none',
              }}
            >
              Freakland
            </Typography>
          )}
        </Box>

        {/* Add button */}
        {showAddButton && (
          <Box sx={{ px: 1, pt: 1.5, pb: 0.5 }}>
            {/* on the freaks feed "Add" opens Quick start (track / blast / full editor) */}
            {pathname === '/freak'
              ? <QuickCreateButton renderTrigger={openQuickCreate => renderAddButton(openQuickCreate)}/>
              : renderAddButton(addTarget)}
          </Box>
        )}

        {/* Main nav items */}
        <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', pt: showAddButton ? 0.5 : 1.5 }}>
          <List disablePadding>
            <NavItem label={translation.leftNavigation.freaks} icon={getFreakIcon(context)} active={pathname === '/freak'} onClick={() => navigate('/freak')} drawerOpen={open} />
            <NavItem label={translation.leftNavigation.places} icon={<TravelExploreIcon />} active={pathname === '/places'} onClick={() => navigate('/places')} drawerOpen={open} />
            <NavItem label={translation.leftNavigation.hub} icon={<HubIcon />} active={pathname === '/freak-hub'} onClick={() => navigate('/freak-hub')} drawerOpen={open} />
            <NavItem label={translation.leftNavigation.campaigns} icon={<CampaignIcon />} active={pathname === '/campaigns'} onClick={() => navigate('/campaigns')} drawerOpen={open} />
            <NavItem label="Blasts" icon={<LocalFireDepartmentIcon />} active={pathname === '/blasts'} onClick={() => navigate('/blasts')} drawerOpen={open} />

            {showFilters && (
              <NavItem label={translation.leftNavigation.filters} icon={<FilterAltIcon />} active={false} onClick={() => setOpenModal(true)} drawerOpen={open} badgeCount={filtersBadgeCount} />
            )}

            <Divider sx={{ my: 1, mx: 2, borderColor: 'rgba(255,255,255,0.07)' }} />

            {CHAT_ENABLED && <NavItem label="Chat" icon={<ChatIcon />} active={pathname.startsWith('/chat')} onClick={() => navigate('/chat')} drawerOpen={open} />}
            <NavItem label={translation.leftNavigation.notifications} icon={<NotificationsNoneIcon />} active={pathname === '/notifications'} onClick={() => navigate('/notifications')} drawerOpen={open} badgeCount={notifCount} />
            <NavItem label={translation.leftNavigation.theFreaks} icon={<SportsMmaIcon />} active={pathname === '/the-freaks'} onClick={() => navigate('/the-freaks')} drawerOpen={open} />
            <NavItem label={translation.leftNavigation.topFreaks} icon={<EmojiEventsIcon />} active={pathname === '/top-freaks'} onClick={() => navigate('/top-freaks')} drawerOpen={open} />
            <NavItem label={translation.leftNavigation.context} icon={getFreakIcon(context)} active={pathname === '/context-change'} onClick={() => navigate('/context-change')} drawerOpen={open} />
          </List>
        </Box>

        {/* Profile pinned at bottom */}
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.07)' }} />
        <Tooltip title={!open ? (props.currentUser?.username || 'Profile') : ''} placement="right" arrow>
          <Box
            onClick={() => navigate(`/@${props.currentUser?.username}`)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: open ? 2 : 1.25,
              py: 1.25,
              cursor: 'pointer',
              transition: 'background 0.15s',
              '&:hover': { backgroundColor: 'rgba(54,201,162,0.07)' },
            }}
          >
            <Avatar
              alt={props.currentUser?.username}
              src={props.currentUser?.imageUrl}
              sx={{ width: 36, height: 36, flexShrink: 0, border: `2px solid ${DEFAULT_THEME_COLOR}33` }}
            />
            {open && (
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ color: 'white', fontWeight: 600, fontSize: '0.875rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {props.currentUser?.username}
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem' }}>
                  Profile
                </Typography>
              </Box>
            )}
          </Box>
        </Tooltip>
      </Drawer>

      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 700,
          bgcolor: 'background.paper',
          boxShadow: 24,
          borderRadius: 2,
        }}>
          <FiltersComponent pathname={pathname} setOpen={() => setOpenModal(false)} />
        </Box>
      </Modal>
    </Box>
  );
};

const mapStateToProps = state => ({
  ...state,
  currentUser: state.common.currentUser,
  context: state.common.context,
  unreadMessagesCount: state.common.unreadMessagesCount,
  publicFreakPlaceFilter: state.common.publicFreakPlaceFilter,
  privateFreakPlaceFilter: state.common.privateFreakPlaceFilter,
  sortByDateFilter: state.common.sortByDateFilter,
  sortByLikesFilter: state.common.sortByLikesFilter,
  filterByNameFilter: state.common.filterByNameFilter,
  filterByDateFromFilter: state.common.filterByDateFromFilter,
  filterByDateToFilter: state.common.filterByDateToFilter,
  filterByStatusFilter: state.common.filterByStatusFilter,
  filterByParticipantFilter: state.common.filterByParticipantFilter,
});

const mapDispatchToProps = dispatch => ({
  onLoad: payload => dispatch({ type: BOTTOM_NAVIGATION_LOADED, payload }),
  setUnreadMessages: payload => dispatch({ type: BOTTOM_NAVIGATION_SET_UNREAD_MESSAGES, payload }),
  setLeftNavigationOpen: payload => dispatch({ type: LEFT_NAVIGATION_OPEN, payload }),
});

export default connect(mapStateToProps, mapDispatchToProps)(LeftNavigationComponent);
